import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Gift, Search, Sparkles, Tag } from "lucide-react";
import { MerchantApi } from "@/api";
import { Input } from "@/components/ui/input";
import { EnhancedPaginationControl } from "@/components/common/EnhancedPaginationControl";
import { useDebounce } from "@/hooks/use-debounce";
import { PublicOfferCard } from "./shared/PublicOfferCard";
import { PublicOfferDetailModal } from "./shared/PublicOfferDetailModal";
import type {
  GetProgramDetailsOfferPointResponse,
  PublicOffer,
} from "./shared/publicOffer.types";
import {
  mapProgramOfferToPublicOffer,
  resolvePublicAssetUrl,
} from "./shared/publicOffer.utils";

/** Shared accent gradient for header bar + footer */
const BRAND_GRADIENT =
  "linear-gradient(135deg, #0c4a6e 0%, #0369a1 38%, #0284c7 68%, #0ea5e9 100%)";

/** Hero — soft indigo/violet wash, ties to offer card accents */
const HERO_GRADIENT =
  "linear-gradient(175deg, #fafafa 0%, #f5f3ff 22%, #eef2ff 55%, #f8fafc 88%, #ffffff 100%)";

const LOYYAL_LOGO_URL = "/offers/loyyal1.png";
const SEARCH_DEBOUNCE_MS = 500;
const PUBLIC_OFFERS_PAGE_SIZE = 10;

export default function PublicOffers() {
  const [searchParams] = useSearchParams();
  const programCode = (searchParams.get("programCode") || "").trim();

  const apiBaseUrl = useMemo(
    () => (import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL || "").trim(),
    [],
  );

  const [offers, setOffers] = useState<PublicOffer[]>([]);
  const [totalOfferCount, setTotalOfferCount] = useState(0);
  const [programTitle, setProgramTitle] = useState<string | null>(null);
  const [programLogoDisplayUrl, setProgramLogoDisplayUrl] = useState<
    string | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [detailOffer, setDetailOffer] = useState<PublicOffer | null>(null);
  const [nameFilter, setNameFilter] = useState("");
  const [searchTitle, setSearchTitle] = useState("");
  const [pageNumber, setPageNumber] = useState(0);
  const debouncedNameFilter = useDebounce(nameFilter, SEARCH_DEBOUNCE_MS);

  const totalPages = Math.max(
    1,
    Math.ceil(totalOfferCount / PUBLIC_OFFERS_PAGE_SIZE),
  );

  useEffect(() => {
    setNameFilter("");
    setPageNumber(0);
  }, [programCode]);

  useEffect(() => {
    setSearchTitle(debouncedNameFilter.trim());
  }, [debouncedNameFilter]);

  useEffect(() => {
    setPageNumber(0);
  }, [searchTitle]);

  useEffect(() => {
    let cancelled = false;
    const isBackgroundFetch = searchTitle.length > 0 || pageNumber > 0;

    (async () => {
      if (!programCode) {
        if (!cancelled) {
          setLoading(false);
          setLoadError(false);
          setProgramTitle(null);
          setProgramLogoDisplayUrl(null);
          setOffers([]);
          setTotalOfferCount(0);
        }
        return;
      }

      if (isBackgroundFetch) {
        setSearchLoading(true);
      } else {
        setLoading(true);
        setLoadError(false);
        setProgramTitle(null);
        setProgramLogoDisplayUrl(null);
        setOffers([]);
        setTotalOfferCount(0);
      }

      try {
        const res = await MerchantApi.getProgramDetailsOfferPoint({
          programCode,
          pageNumber,
          pageSize: PUBLIC_OFFERS_PAGE_SIZE,
          title: searchTitle,
        });
        if (cancelled) return;

        const data = (res?.data || {}) as GetProgramDetailsOfferPointResponse;
        const program = data.programVO;
        const programName = (program?.programName || "").trim() || "Program";
        const totalOffers = Number(data.totalNumberOfProgramOffers);
        const resolvedTotal = Number.isFinite(totalOffers) ? totalOffers : 0;

        if (!isBackgroundFetch) {
          setProgramTitle(programName);

          const programLogoUrl = resolvePublicAssetUrl(
            apiBaseUrl,
            program?.programLogoUri,
          );
          setProgramLogoDisplayUrl(programLogoUrl || null);
        }

        const programLogoUrl = resolvePublicAssetUrl(
          apiBaseUrl,
          program?.programLogoUri,
        );
        const merchants = data.merchantVOList || [];
        const defaultMerchantImageUrl = resolvePublicAssetUrl(
          apiBaseUrl,
          merchants.find((m) => (m.merchantImageUri || "").trim())
            ?.merchantImageUri,
        );

        const rawList = Array.isArray(data.merchantOffersVOList)
          ? data.merchantOffersVOList
          : [];

        const mappedOffers = rawList.map((item) =>
          mapProgramOfferToPublicOffer(item, {
            apiBaseUrl,
            programName,
            programLogoUrl,
            defaultMerchantImageUrl,
          }),
        );

        setOffers(mappedOffers);
        setTotalOfferCount(resolvedTotal);
      } catch {
        if (!cancelled) {
          if (isBackgroundFetch) {
            setOffers([]);
          } else {
            setLoadError(true);
            setOffers([]);
            setProgramLogoDisplayUrl(null);
            setTotalOfferCount(0);
          }
        }
      } finally {
        if (!cancelled) {
          if (isBackgroundFetch) {
            setSearchLoading(false);
          } else {
            setLoading(false);
          }
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [programCode, searchTitle, pageNumber, apiBaseUrl]);

  const showOffersGrid =
    !loading && programCode && !loadError && totalOfferCount > 0;
  const hasActiveFilter = searchTitle.length > 0;

  return (
    <div className="h-screen min-h-0 overflow-x-hidden overflow-y-auto">
      <PublicOfferDetailModal
        offer={detailOffer}
        onOpenChange={(open) => {
          if (!open) setDetailOffer(null);
        }}
      />
      <div className="flex min-h-full w-full flex-col bg-white">
        {/* ── Branded top bar ── */}
        <div
          className="relative z-20 shrink-0 text-white shadow-lg shadow-sky-900/20"
          style={{ backgroundImage: BRAND_GRADIENT }}
        >
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute -right-16 -top-16 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
            <div className="absolute -bottom-10 left-1/4 h-32 w-32 rounded-full bg-cyan-300/15 blur-2xl" />
          </div>

          <div className="relative mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-3.5 sm:px-6 lg:px-8">
            <div className="flex min-w-0 items-center gap-3">
              {programLogoDisplayUrl ? (
                <img
                  src={programLogoDisplayUrl}
                  alt={programTitle ? `${programTitle} logo` : "Program logo"}
                  className="h-10 w-10 shrink-0 rounded-lg border border-white/30 bg-white object-contain p-0.5 shadow-sm sm:h-11 sm:w-11"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.style.visibility = "hidden";
                  }}
                />
              ) : (
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-white/25 bg-white/15 sm:h-11 sm:w-11">
                  <Gift className="h-5 w-5 text-white/90" aria-hidden />
                </div>
              )}
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold leading-tight sm:text-base">
                  {programTitle || "Rewards Program"}
                </p>
                {programCode ? (
                  <p className="truncate text-xs text-sky-100/80">
                    {programCode}
                  </p>
                ) : null}
              </div>
            </div>

            {!loading && programCode && !loadError && totalOfferCount > 0 ? (
              <div className="hidden shrink-0 items-center gap-2 rounded-full border border-white/25 bg-white/10 px-3 py-1.5 text-xs font-medium backdrop-blur-sm sm:flex">
                <Tag className="h-3.5 w-3.5 text-sky-200" aria-hidden />
                <span>
                  {totalOfferCount} offer{totalOfferCount === 1 ? "" : "s"}{" "}
                  available
                </span>
              </div>
            ) : null}
          </div>
        </div>

        {/* ── Hero ── */}
        <header
          className="relative flex min-h-[36vh] shrink-0 flex-col justify-center overflow-hidden px-6 py-12 md:min-h-[44vh] md:py-16"
          style={{ backgroundImage: HERO_GRADIENT }}
        >
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute -left-20 -top-20 h-64 w-64 rounded-full bg-violet-200/30 blur-3xl" />
            <div className="absolute -bottom-16 right-1/4 h-48 w-48 rounded-full bg-indigo-200/25 blur-3xl" />
          </div>
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-10 md:h-12"
            style={{
              background: "linear-gradient(to top, #ffffff, transparent)",
            }}
            aria-hidden
          />

          <div className="relative z-10 mx-auto max-w-3xl text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-violet-200/80 bg-white/80 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-violet-700 shadow-sm">
              <Sparkles className="h-3.5 w-3.5 text-violet-500" aria-hidden />
              Offer Point
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-800 sm:text-4xl md:text-5xl md:leading-tight">
              Deals You Can&apos;t{" "}
              <span className="bg-gradient-to-r from-violet-600 to-indigo-600 bg-clip-text text-transparent">
                Ignore
              </span>
            </h1>
            {programTitle ? (
              <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-slate-600 sm:text-base">
                Explore handpicked offers from{" "}
                <span className="font-semibold text-slate-800">
                  {programTitle}
                </span>{" "}
                — save more, earn more, and grab rewards before they&apos;re
                gone.
              </p>
            ) : (
              <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-slate-600 sm:text-base">
                Handpicked offers just for you — save more, earn more. Grab
                exclusive rewards before they&apos;re gone.
              </p>
            )}
          </div>
        </header>

        {/* ── Main content ── */}
        <main className="w-full flex-1 bg-white px-5 pb-10 pt-4 sm:px-6 md:pb-14 md:pt-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            {loading ? (
              <p className="py-16 text-center text-sm text-slate-600">
                Loading offers…
              </p>
            ) : !programCode ? (
              <p className="py-16 text-center text-sm text-slate-600">
                This page needs a program code in the URL query string. Open{" "}
                <span className="font-semibold">View Offers</span> from your
                Offer Point program list to open this page with the correct
                link.
              </p>
            ) : loadError ? (
              <p className="py-16 text-center text-sm text-slate-600">
                We couldn&apos;t load offers right now. Please try again later.
              </p>
            ) : totalOfferCount === 0 && !hasActiveFilter ? (
              <p className="py-16 text-center text-sm text-slate-600">
                No offers available at the moment.
              </p>
            ) : (
              <>
                {/* Filter bar */}
                <div className="mb-8 rounded-xl border border-slate-200 bg-slate-50 p-4 shadow-sm sm:p-5">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h2 className="text-lg font-bold text-slate-800 sm:text-xl">
                        Browse Offers
                      </h2>
                      <p className="mt-0.5 text-sm text-slate-500">
                        Filter by offer name to find what you&apos;re looking
                        for.
                      </p>
                    </div>
                    <div className="relative w-full sm:max-w-sm">
                      <Search
                        className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                        aria-hidden
                      />
                      <Input
                        type="search"
                        placeholder="Search by offer name…"
                        value={nameFilter}
                        onChange={(e) => setNameFilter(e.target.value)}
                        className="h-11 border-slate-200 bg-white pl-10 shadow-sm transition-shadow focus-visible:shadow-md"
                        aria-label="Filter offers by name"
                      />
                    </div>
                  </div>

                  {hasActiveFilter ? (
                    <p className="mt-3 text-sm text-slate-500">
                      Showing{" "}
                      <span className="font-semibold text-slate-700">
                        {offers.length}
                      </span>{" "}
                      of {totalOfferCount} offer
                      {totalOfferCount === 1 ? "" : "s"}
                      {offers.length === 0 ? (
                        <span className="text-slate-600">
                          {" "}
                          — try a different search term
                        </span>
                      ) : null}
                    </p>
                  ) : null}
                </div>

                {searchLoading ? (
                  <p className="py-16 text-center text-sm text-slate-600">
                    Loading offers…
                  </p>
                ) : offers.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 py-16 text-center">
                    <Search
                      className="mx-auto mb-3 h-8 w-8 text-sky-300"
                      aria-hidden
                    />
                    <p className="text-sm font-medium text-slate-700">
                      No offers match &ldquo;{searchTitle}&rdquo;
                    </p>
                    <p className="mt-1 text-sm text-slate-500">
                      Clear the search or try another offer name.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                      {offers.map((offer) => (
                        <PublicOfferCard
                          key={offer.id}
                          offer={offer}
                          onOpenDetail={setDetailOffer}
                        />
                      ))}
                    </div>
                    <EnhancedPaginationControl
                      totalPages={totalPages}
                      currentPage={pageNumber}
                      onPageChange={setPageNumber}
                      className="mt-8"
                    />
                  </>
                )}
              </>
            )}
          </div>
        </main>

        {/* ── Branded footer ── */}
        <footer
          className="relative mt-auto shrink-0 text-white"
          style={{ backgroundImage: BRAND_GRADIENT }}
        >
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute -left-20 bottom-0 h-48 w-48 rounded-full bg-white/8 blur-3xl" />
            <div className="absolute -right-10 top-0 h-36 w-36 rounded-full bg-cyan-300/10 blur-2xl" />
          </div>

          <div className="relative mx-auto max-w-7xl px-5 py-10 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-8 md:flex-row md:items-start md:justify-between">
              <div className="max-w-md">
                <div className="flex items-center gap-3">
                  {programLogoDisplayUrl ? (
                    <img
                      src={programLogoDisplayUrl}
                      alt=""
                      className="h-12 w-12 rounded-lg border border-white/25 bg-white object-contain p-1"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.style.visibility = "hidden";
                      }}
                    />
                  ) : null}
                  <div>
                    <p className="text-lg font-bold leading-tight">
                      {programTitle || "Rewards Program"}
                    </p>
                    {programCode ? (
                      <p className="mt-0.5 text-sm text-sky-100/75">
                        {programCode}
                      </p>
                    ) : null}
                  </div>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-sky-100/85">
                  Exclusive member offers curated for you. Browse deals, unlock
                  rewards, and save on your favorite brands.
                </p>
                {showOffersGrid ? (
                  <p className="mt-3 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-medium text-sky-50">
                    <Sparkles className="h-3.5 w-3.5" aria-hidden />
                    {totalOfferCount} active offer
                    {totalOfferCount === 1 ? "" : "s"} in this program
                  </p>
                ) : null}
              </div>

              <div className="flex flex-col items-start gap-3 md:items-end">
                <p className="text-xs font-medium uppercase tracking-wider text-sky-200/70">
                  Powered by
                </p>
                <span className="inline-flex items-center bg-white p-1">
                  <img
                    src={LOYYAL_LOGO_URL}
                    alt="Loyyal"
                    className="block h-9 w-auto max-w-[140px] bg-white object-contain object-left sm:h-10 sm:max-w-[160px]"
                    style={{ backgroundColor: "#ffffff" }}
                  />
                </span>
                <p className="text-sm text-sky-100/70">
                  Premium loyalty &amp; rewards platform
                </p>
              </div>
            </div>

            <div className="mt-8 border-t border-white/15 pt-6 text-center text-xs text-sky-100/60 sm:text-sm">
              &copy; {new Date().getFullYear()}{" "}
              {programTitle ? `${programTitle} · ` : ""}
              Loyyal. All rights reserved.
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
